// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, inject, Injector, OnDestroy, signal, ViewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {BrowserQRCodeReader, ChecksumException, FormatException, Result} from "@zxing/library";
import {FormControl, Validators} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export interface DialogData_ScanQRCodeDialogInput extends MatDialogDataBaseInput {}

export interface DialogData_ScanQRCodeDialogOutput extends MatDialogDataBaseOutput {
	scannedQRCodeValue?: string;
}

@Component({
	selector: "sim-scan-qr-code-dialog",
	templateUrl: "./scan-qr-code-dialog.component.html",
	styleUrl: "./scan-qr-code-dialog.component.scss",
	standalone: false,
})
export class ScanQrCodeDialogComponent
	extends BasicMaterialDialog<ScanQrCodeDialogComponent, DialogData_ScanQRCodeDialogInput, DialogData_ScanQRCodeDialogOutput>
	implements AfterViewInit, OnDestroy
{
	private readonly REAR_CAMERA_SOURCE_MATCHER = ["back", "rear", "BACK", "REAR", "Back", "Rear"].join("|");

	private _codeReader: BrowserQRCodeReader;
	private _selectedDeviceId: string;

	public sourceOptions: {label: string; value: string}[] = [];
	public qrCodeScanResult: string;
	public sourceOptionFC: FormControl = new FormControl("", Validators.required);

	private _translate = inject(TranslateService);

	public isLoadingVideoSource = signal<boolean>(true);

	@ViewChild("videoElement") videoElement: ElementRef;
	statusText = signal<string | null>(this._translate.instant(Translations.SCAN_QR_CODE_DIALOG_LOADING_SOURCES));

	constructor(_injector: Injector) {
		super(_injector);
	}

	ngOnDestroy(): void {
		this._codeReader.reset();
	}

	ngAfterViewInit(): void {
		this.sourceOptionFC.valueChanges.subscribe((value) => {
			if (value) {
				this._selectedDeviceId = value;
				this.resetQRCodeScanner();
			}
		});

		this.initQRCodeReader();
	}

	private initQRCodeReader() {
		this._codeReader = new BrowserQRCodeReader();

		this._codeReader
			.listVideoInputDevices()
			.then((videoInputDevices) => {
				this.evaluateDefaultVideoInputDevice(videoInputDevices);
				this.sourceOptionFC.setValue(this._selectedDeviceId);
				if (videoInputDevices.length >= 1) {
					videoInputDevices.forEach((element) => {
						this.sourceOptions.push({label: element.label, value: element.deviceId});
					});
				}

				this.decodeContinuously();
			})
			.catch((err) => {
				console.error(err);
			});
	}

	private decodeContinuously() {
		this._codeReader
			.decodeFromVideoDevice(this._selectedDeviceId, "video", (result: Result, err) => {
				this.isLoadingVideoSource.set(false);

				this.statusText.set(this._translate.instant(Translations.SCAN_QR_CODE_DIALOG_SEARCHING_FOR_QR_CODE));

				if (result) {
					// properly decoded qr code
					this.qrCodeScanResult = result.getText();
					this.videoElement.nativeElement.pause();

					this.statusText.set(
						this._translate.instant(Translations.SCAN_QR_CODE_DIALOG_QR_CODE_FOUND, {qrCodeValue: this.qrCodeScanResult}),
					);

					setTimeout(() => {
						this.close({scannedQRCodeValue: this.qrCodeScanResult, action: "ok"});
					}, 1000);
				}

				if (err) {
					// As long as this error belongs into one of the following categories
					// the code reader is going to continue as excepted. Any other error
					// will stop the decoding loop.
					//
					// Excepted Exceptions:
					//
					//  - NotFoundException
					//  - ChecksumException
					//  - FormatException

					//Can be used for debug purposes
					// if (err instanceof NotFoundException) {
					//	console.log("No code could be recognized.");
					// }

					if (err instanceof ChecksumException) {
						console.error("A code was found, but it's read value was not valid.");
					}

					if (err instanceof FormatException) {
						console.error("A code was found, but it was in a invalid format.");
					}
				}
			})
			.then();
	}

	private evaluateDefaultVideoInputDevice(videoInputDevices: MediaDeviceInfo[]) {
		if (videoInputDevices?.length === 0) {
			return;
		}

		if (videoInputDevices.length === 1) {
			this._selectedDeviceId = videoInputDevices[0].deviceId;
			return;
		}

		//If multiple sources have been detected, check if any of those sources have typical labels to recognize the rear camera.
		for (const videoInputDevice of videoInputDevices) {
			if (videoInputDevice.label.match(this.REAR_CAMERA_SOURCE_MATCHER)) {
				this._selectedDeviceId = videoInputDevice.deviceId;
				return;
			}
		}

		this._selectedDeviceId = videoInputDevices[0].deviceId;
	}

	resetQRCodeScanner() {
		this.isLoadingVideoSource.set(true);
		this.statusText.set(this._translate.instant(Translations.SCAN_QR_CODE_DIALOG_LOADING_SOURCES));
		this._codeReader.reset();
		this.decodeContinuously();
	}
}
